import type { CameraTamper, MapPlacement } from './api'
import type { CameraStatus } from './types'
import { tamperLabel } from './tamperSignal'

// How wide a camera with a direction but no field of view is drawn on the
// dispatcher's map. The Overwatch Console's editor draws the same width
// (its src/components/siteMap/siteMap.ts), and nothing checks the two agree.
export const DEFAULT_FOV = 60
// How far a cone reaches from its camera on screen, in pixels, when its camera
// has no length set.
export const CONE_REACH = 56

// A cone has to read on an aerial photo, against pale car parks and dark roofs
// alike, which no single fill does: every cone carries a white edge over a dark
// halo. Chosen 2026-10-07 against a real site's plan.
export const CONE = {
  watched: 'rgba(245,166,35,0.45)',
  other: 'rgba(161,161,170,0.35)',
  edge: '#ffffff',
  halo: 'rgba(0,0,0,0.65)',
}

/** How far a cone extends on screen: its camera's share of the plan as drawn, or the fallback. */
export function coneReach(p: { reach?: number | null }, planWidth: number, fallback: number): number {
  return p.reach != null && planWidth > 0 ? p.reach * planWidth : fallback
}

const unit = (v: number) => String(Math.round(v * 10000) / 10000).replace(/^-0$/, '0')

/**
 * The cone as an SVG path in a unit circle about its camera: a wedge fov
 * degrees wide, centred on direction, which is clockwise from the top.
 */
export function wedgePath(direction: number, fov: number): string {
  const point = (deg: number) => {
    const a = (deg * Math.PI) / 180
    return `${unit(Math.sin(a))} ${unit(-Math.cos(a))}`
  }
  return `M0 0 L${point(direction - fov / 2)} A1 1 0 0 1 ${point(direction + fov / 2)} Z`
}

// Positions are kept to four decimals: finer than any plan is drawn, and it
// keeps float noise (0.2 × 100 = 20.000000000000004) out of styles.
export const tidy = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 10000) / 10000

/** Where a placement sits on its plan, as CSS positions. */
export const positionOf = (p: MapPlacement) => ({ left: `${tidy(p.x) * 100}%`, top: `${tidy(p.y) * 100}%` })

export interface CameraState {
  health: CameraStatus
  tamper?: CameraTamper
  inProgress: boolean
}

const HEALTH_WORDS: Partial<Record<CameraStatus, string>> = { online: 'Online', offline: 'Offline', degraded: 'Degraded' }

/** What a camera's marker, row or card says about it, in the order a dispatcher weighs it. */
export function describe(s: CameraState): string[] {
  return [
    HEALTH_WORDS[s.health],
    s.tamper ? tamperLabel(s.tamper) : undefined,
    s.inProgress ? 'incident in progress' : undefined,
  ].filter((w): w is string => !!w)
}

export function nameOf(camera: string, place?: string): string {
  return place ? `${camera} · ${place}` : camera
}

/**
 * A camera's accessible name: who it is, its state, and whether it is the one
 * being watched, so a marker and a list row read the same — and so mobile's
 * markers read the same as these, which ruleParity.test.ts holds them to.
 */
export function cameraLabel(camera: string, place: string | undefined, state: CameraState, watching: boolean): string {
  const words = describe(state)
  return `${nameOf(camera, place)}${words.length ? ` — ${words.join(', ')}` : ''}${watching ? ' (watching)' : ''}`
}
